import React from "react";
import { Link } from "react-router-dom";
import DocsLayout, { Section, Callout } from "./DocsLayout";

const CATEGORIES = [
  {
    path: "/docs/getting-started",
    label: "Getting Started",
    color: "#0ea5e9",
    icon: "👋",
    desc: "Logging in, security, and the AI chat assistant.",
  },
  {
    path: "/docs/ai-assistant",
    label: "AI Chat Assistant",
    color: "#ec4899",
    icon: "🤖",
    desc: "What Aria knows by role, what actions it can perform, and usage limits.",
  },
  {
    path: "/docs/recruitment",
    label: "Recruitment",
    color: "#7c3aed",
    icon: "🎯",
    desc: "Jobs, AI JD generation, candidates, AI scoring, pipeline, talent pool, interviews, video screening, offers.",
  },
  {
    path: "/docs/people-hr",
    label: "People & HR",
    color: "#059669",
    icon: "🗂️",
    desc: "Leaves, attendance, timesheet, employees, performance, offboarding, helpdesk, and more.",
  },
  {
    path: "/docs/finance",
    label: "Finance",
    color: "#d97706",
    icon: "💰",
    desc: "Invoices, Thai sales documents, vendor bills, AI expense capture, payroll.",
  },
  {
    path: "/docs/admin-settings",
    label: "Admin & Settings",
    color: "#1e3a8a",
    icon: "⚙️",
    desc: "Company configuration, feature toggles, workspace, and billing.",
  },
];

const ROLE_MATRIX: [string, string, string, string, string, string, string][] = [
  //  Module                          Employee    HR          Recruiter   Admin Asst  Finance     Admin
  ["My Leaves / Attendance / Timesheet", "✓", "✓", "✓", "✓", "✓", "✓"],
  ["Salary Slips (own)",                "✓", "✓", "✓", "—", "✓", "✓"],
  ["Leave Management (approve)",        "—", "—", "—", "✓", "—", "✓"],
  ["Payroll (generate)",                "—", "—", "—", "✓", "—", "✓"],
  ["Employees / Org Chart",             "—", "—", "—", "✓", "—", "✓"],
  ["Jobs / Candidates / Offers",        "—", "✓", "✓", "—", "—", "✓"],
  ["Talent Pool / Interview Hub / AI Screening", "—", "✓", "✓", "—", "—", "✓"],
  ["Invoices & Quotations",             "—", "—", "—", "—", "✓", "✓"],
  ["Vendors / AI Expense Capture",      "—", "—", "—", "—", "✓", "✓"],
  ["Performance Reviews",               "✓ (own)", "—", "—", "✓", "—", "✓"],
  ["Reports",                           "—", "—", "—", "✓", "—", "✓"],
  ["HR Letters",                        "—", "—", "—", "✓", "—", "✓"],
  ["Settings / Feature Access",         "—", "—", "—", "—", "—", "✓"],
  ["Workspace",                         "—", "—", "—", "✓", "—", "✓"],
  ["AI Chat (see AI Chat Assistant page)", "✓ (10/day)", "✓", "✓", "✓", "✓", "✓"],
];

export default function DocsIndex() {
  return (
    <DocsLayout>
      <div style={{ marginBottom: 40 }}>
        <h1 style={{ fontSize: 32, fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>
          HRxAI Documentation
        </h1>
        <p style={{ fontSize: 16, color: "#64748b", maxWidth: 620, lineHeight: 1.6 }}>
          HRxAI is an AI-powered HR platform for managing your entire workforce — from hiring to
          offboarding, payroll, and finance. Docs are organized by feature below — each page notes which
          roles can use it.
        </p>
      </div>

      <Section title="What is HRxAI?">
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.8, marginBottom: 16 }}>
          HRxAI brings together recruitment, HR operations, payroll, and finance into one platform.
          Every user sees only the modules relevant to their role — keeping things simple and secure.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 8 }}>
          {[
            { icon: "🏢", label: "Multi-tenant", desc: "Fully isolated per company" },
            { icon: "🤖", label: "AI-powered", desc: "JD generation, CV scoring, HR chat" },
            { icon: "🔒", label: "Secure", desc: "TOTP, passkeys, encrypted storage" },
            { icon: "📧", label: "Email alerts", desc: "Approvals, payslips, invites" },
          ].map(f => (
            <div key={f.label} style={{
              background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8,
              padding: "14px 16px", display: "flex", gap: 12, alignItems: "flex-start",
            }}>
              <span style={{ fontSize: 20 }}>{f.icon}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{f.label}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Browse by Feature">
        <p style={{ fontSize: 14, color: "#64748b", marginBottom: 20 }}>
          Every feature is documented once, with an "Available to" tag showing which roles can use it —
          instead of separate guides per role.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {CATEGORIES.map(c => (
            <Link key={c.path} to={c.path} style={{ textDecoration: "none" }}>
              <div style={{
                background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
                padding: "16px 20px", display: "flex", alignItems: "center", gap: 16,
                transition: "box-shadow 0.15s",
              }}
                onMouseEnter={e => (e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)")}
                onMouseLeave={e => (e.currentTarget.style.boxShadow = "none")}
              >
                <div style={{
                  width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                  background: c.color + "15",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 22,
                }}>{c.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>{c.label}</div>
                  <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>{c.desc}</div>
                </div>
                <div style={{ color: c.color, fontWeight: 700, fontSize: 18 }}>→</div>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      <Section title="Logging In">
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 12 }}>
          There's no separate signup step — your company invites you by email. See{" "}
          <Link to="/docs/getting-started" style={{ color: "#0ea5e9", fontWeight: 600 }}>Getting Started</Link>{" "}
          for the full walkthrough, including security setup (TOTP, passkeys).
        </p>
        <Callout
          icon="💡"
          title="First time?"
          body="You'll receive a setup link by email from your admin. Click it to confirm your identity and activate your account. The link is valid for a limited time."
        />
      </Section>

      <Section title="Which Role Can Do What?">
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          A quick reference — every role also has full access to Getting Started (login, security, AI chat).
          Recruiter has the same access as HR for recruitment features, plus Employee-level self-service.
        </p>
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" as any }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 640 }}>
            <thead>
              <tr style={{ background: "#f1f5f9" }}>
                {["Module", "Employee", "HR", "Recruiter", "Admin Assistant", "Finance", "Admin"].map(h => (
                  <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700, color: "#0f172a", borderBottom: "2px solid #e2e8f0" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROLE_MATRIX.map((row, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                  {row.map((cell, j) => (
                    <td key={j} style={{
                      padding: "9px 12px", borderBottom: "1px solid #e2e8f0",
                      color: j === 0 ? "#0f172a" : cell.startsWith("✓") ? "#059669" : cell === "—" ? "#cbd5e1" : "#0f172a",
                      fontWeight: j === 0 ? 600 : 400,
                    }}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </DocsLayout>
  );
}
